/*
 * Asset Library launcher — the task pane's half of the Office Dialog
 * conversation, so DeckorateModule only has to call `open()`.
 *
 * The Asset Library lives in the dialog WINDOW for the whole session (its
 * own loading/error/Retry/insert-result UI). The pane only does what a
 * dialog window physically cannot: fetch the slide-template manifest and org
 * brand templates (no AuthStateProvider in a dialog, so no bearer token),
 * insert via PowerPoint.run() (never available in a dialog), and close the
 * window.
 *
 * The in-pane overlay is used ONLY where there's no Office Dialog API at all
 * (browser dev, tests).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { logEvent } from "../../../../../telemetry";
import { useDeckorateHttp } from "../../../hooks/useDeckorateHttp";
import { HttpStatusError, type HttpClient } from "../../../services/deckorateApi";
import {
  fetchSlideTemplatesFromSasInfo,
  requestSlideTemplatesSasInfo,
  logSlideTemplatesError,
  type SlideTemplate,
  type SlideTemplatesSasInfo,
} from "../../slide-templates/services/slideTemplatesApi";
import { insertSlideTemplates } from "../../slide-templates/services/slideTemplateInsert";
import { getErrorMessage } from "../../PhotoHeadshot/services/photoHeadApi";
import {
  fetchSavedBrandTemplates,
  logSavedBrandTemplatesError,
  type SavedBrandTemplate,
} from "../../apply-brand/services/brandTemplatesApi";
import { setPendingSavedTemplateSelection } from "../../apply-brand/services/pendingSavedTemplateSelection";
import { requestDeckorateView } from "../../../../../../integrations/tools/deckorate";
import {
  fetchPresentationTemplatesFromSasInfo,
  requestDirectorySasInfo,
  logPresentationTemplatesError,
  type DirectorySasInfo,
  type PresentationTemplate,
} from "../../presentation-templates/services/presentationTemplatesApi";
import { insertPresentationTemplates } from "../../presentation-templates/services/presentationTemplateInsert";
import {
  FALLBACK_INSERT_ERROR,
  FALLBACK_SAS_ERROR,
  FALLBACK_BRAND_TEMPLATES_ERROR,
  FALLBACK_PRESENTATION_SAS_ERROR,
  FALLBACK_PRESENTATION_INSERT_ERROR,
  type DialogToPaneMessage,
} from "../services/assetLibraryDialogChannel";
import { clearBrandTemplatesHandoff, writeBrandTemplatesHandoff } from "../services/brandTemplatesHandoff";
import { clearPendingBrandTemplatePreview } from "../services/brandTemplatePreviewHandoff";
import { canUseDialog, openAssetLibraryDialog, type AssetLibrarySession } from "./openAssetLibraryDialog";

const DECKORATE_CONNECTOR = "deckorate";

const BRAND_TEMPLATES_HANDOFF_FAILED_MESSAGE = "Couldn't prepare the brand template list. Please try again.";
/**
 * Shown in the DIALOG's own error+Retry state (not a pane toast) on a 401 that
 * survived `fetchWithRefresh`'s own silent-refresh retry. Most commonly hit
 * right after a task-pane reload — the window opens and asks for the manifest
 * before AuthStateProvider has finished re-acquiring a token, so the very
 * first request loses that race even though sign-in itself is fine. Retry
 * (same button as any other manifest error) replays the request once the
 * token has caught up, so this must never close the window — the earlier
 * behaviour (close + a pane toast telling the user to sign in again) treated
 * that race as if the session had actually expired.
 */
const SESSION_STILL_STARTING_MESSAGE = "Couldn't load the slide template list yet — your session may still be starting. Try again in a moment.";

function isAuthFailure(error: unknown): boolean {
  return error instanceof HttpStatusError && error.status === 401;
}

export interface AssetLibraryLauncher {
  /** Opens the Asset Library dialog window. */
  readonly open: () => void;
  /**
   * True only where there's no Office Dialog API at all (browser dev, tests),
   * so the caller renders the in-pane overlay instead. Never true in a real
   * Office host.
   */
  readonly isInlineOpen: boolean;
  readonly closeInline: () => void;
}

/**
 * Serves one "ready"/"refresh": mints a directory SAS token and hands it
 * straight to the window (see assetLibraryDialogChannel.ts's module header).
 * Never throws — the window's error state IS the error report. The pane also
 * fetches its own copy in the background off the same sasInfo for serveInsert
 * (only the pane can call PowerPoint.run()); an insert that races ahead of it
 * just surfaces as insertSlideTemplates' ordinary "no insertable content".
 */
async function serveManifest(
  http: HttpClient,
  session: AssetLibrarySession,
  cache: { current: readonly SlideTemplate[] },
): Promise<void> {
  let sasInfo: SlideTemplatesSasInfo;
  try {
    sasInfo = await requestSlideTemplatesSasInfo(http);
  } catch (error) {
    const message = isAuthFailure(error) ? SESSION_STILL_STARTING_MESSAGE : getErrorMessage(error, FALLBACK_SAS_ERROR);
    logSlideTemplatesError("slide-templates-dialog-sas", message);
    // Stay open — the dialog has its own error state + Retry, a better
    // recovery path than closing and leaving only a pane toast.
    session.send({ type: "sas-error", message });
    return;
  }

  fetchSlideTemplatesFromSasInfo(sasInfo)
    .then((templates) => {
      cache.current = templates;
    })
    .catch((error: unknown) => {
      logSlideTemplatesError("slide-templates-dialog-pane-cache", getErrorMessage(error, FALLBACK_SAS_ERROR));
    });

  session.send({ type: "sas-ready", sasUrl: sasInfo.directorySasUrl, manifestBlobPath: sasInfo.manifestBlobPath });
}

/** Serves one "insert". Never throws, for the same reason as serveManifest. */
async function serveInsert(
  session: AssetLibrarySession,
  cache: { current: readonly SlideTemplate[] },
  ids: readonly string[],
): Promise<void> {
  try {
    const inserted = await insertSlideTemplates(cache.current, ids);
    session.send({ type: "insert-done", inserted });
  } catch (error) {
    const message = getErrorMessage(error, FALLBACK_INSERT_ERROR);
    logSlideTemplatesError("slide-templates-dialog-insert", message);
    session.send({ type: "insert-error", message });
  }
}

/** Serves one "brand-templates-request"/"brand-templates-refresh": fetch the
 *  org list, stage it, and tell the window. Never throws, same as
 *  serveManifest. My Templates (local uploads) never appears here. */
async function serveBrandTemplates(
  http: HttpClient,
  session: AssetLibrarySession,
  cache: { current: readonly SavedBrandTemplate[] },
): Promise<void> {
  let org: readonly SavedBrandTemplate[];
  try {
    org = await fetchSavedBrandTemplates(http);
  } catch (error) {
    const message = isAuthFailure(error) ? SESSION_STILL_STARTING_MESSAGE : getErrorMessage(error, FALLBACK_BRAND_TEMPLATES_ERROR);
    logSavedBrandTemplatesError("brand-templates-dialog-fetch", message);
    session.send({ type: "brand-templates-error", message });
    return;
  }

  cache.current = org;
  if (!writeBrandTemplatesHandoff(org)) {
    session.send({ type: "brand-templates-error", message: BRAND_TEMPLATES_HANDOFF_FAILED_MESSAGE });
    return;
  }
  session.send({ type: "brand-templates-ready" });
}

/** Serves one "switch-brand-template": look the pick up in the last fetch,
 *  park it for ApplyBrandPanel, navigate the pane there, and close this
 *  window. A pick no longer in the cache (stale) is a logged no-op — the
 *  window just stays open on its grid. */
function serveSwitchBrandTemplate(
  session: AssetLibrarySession,
  cache: { current: readonly SavedBrandTemplate[] },
  id: string,
): void {
  const template = cache.current.find((candidate) => candidate.id === id);
  if (!template) {
    logSavedBrandTemplatesError("brand-templates-dialog-switch-stale", `No cached org template for id "${id}"`);
    return;
  }
  setPendingSavedTemplateSelection({ kind: "org", template });
  requestDeckorateView("apply-brand");
  session.close();
}

/**
 * Serves one "presentation-templates-ready"/"presentation-templates-refresh":
 * same shape as serveManifest above, for the Presentation Templates tab. The
 * pane's own background fetch (for servePresentationInsert) races the SAS
 * reply the same way; an early insert just falls back to "no insertable
 * content" there.
 */
async function servePresentationManifest(
  http: HttpClient,
  session: AssetLibrarySession,
  cache: { current: readonly PresentationTemplate[] },
): Promise<void> {
  let sasInfo: DirectorySasInfo;
  try {
    sasInfo = await requestDirectorySasInfo(http);
  } catch (error) {
    const message = isAuthFailure(error)
      ? SESSION_STILL_STARTING_MESSAGE
      : getErrorMessage(error, FALLBACK_PRESENTATION_SAS_ERROR);
    logPresentationTemplatesError("presentation-templates-dialog-sas", message);
    // Stay open — the dialog has its own error state + Retry, a better
    // recovery path than closing and leaving only a pane toast.
    session.send({ type: "presentation-sas-error", message });
    return;
  }

  fetchPresentationTemplatesFromSasInfo(sasInfo)
    .then((presentations) => {
      cache.current = presentations;
    })
    .catch((error: unknown) => {
      logPresentationTemplatesError(
        "presentation-templates-dialog-pane-cache",
        getErrorMessage(error, FALLBACK_PRESENTATION_SAS_ERROR),
      );
    });

  session.send({ type: "presentation-sas-ready", sasUrl: sasInfo.sasUrl, folderPath: sasInfo.folderPath });
}

/** Serves one "presentation-templates-insert". Never throws, for the same
 *  reason as serveInsert. */
async function servePresentationInsert(
  session: AssetLibrarySession,
  cache: { current: readonly PresentationTemplate[] },
  ids: readonly string[],
): Promise<void> {
  const allSlides = cache.current.flatMap((presentation) => presentation.slides);
  try {
    const inserted = await insertPresentationTemplates(allSlides, ids);
    session.send({ type: "presentation-insert-done", inserted });
  } catch (error) {
    const message = getErrorMessage(error, FALLBACK_PRESENTATION_INSERT_ERROR);
    logPresentationTemplatesError("presentation-templates-dialog-insert", message);
    session.send({ type: "presentation-insert-error", message });
  }
}

/**
 * @param onNotice Surfaces a short, user-facing message in the pane. Used only
 *   for failures with no window to show them in — i.e. the window refusing to
 *   open. Must be stable (memoised): it feeds `open`'s identity.
 */
export function useAssetLibraryLauncher(onNotice: (message: string) => void): AssetLibraryLauncher {
  const http = useDeckorateHttp();
  const [isInlineOpen, setIsInlineOpen] = useState(false);

  /** The last manifest we fetched, with its base64 payloads intact. */
  const manifestRef = useRef<readonly SlideTemplate[]>([]);
  /** The last org brand template list we fetched — "switch-brand-template"
   *  looks up the full entry here rather than re-fetching. */
  const brandTemplatesRef = useRef<readonly SavedBrandTemplate[]>([]);
  /** The last presentation-templates manifest we fetched, with its
   *  sourceFileUrl/slideIndex intact. */
  const presentationTemplatesRef = useRef<readonly PresentationTemplate[]>([]);
  /** Live session, so a second open() can't stack a second window. */
  const sessionRef = useRef<AssetLibrarySession | null>(null);
  /** Set between open() and the window appearing, to close the same gap. */
  const isOpeningRef = useRef(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Pane can unmount (navigation, sign-out) with the window still up —
      // close it rather than leaving an orphan, and clear staged data.
      sessionRef.current?.close();
      sessionRef.current = null;
      clearBrandTemplatesHandoff();
      clearPendingBrandTemplatePreview();
    };
  }, []);

  const handleMessage = useCallback(
    (message: DialogToPaneMessage, session: AssetLibrarySession) => {
      const served = (async () => {
        switch (message.type) {
          case "ready":
          case "refresh":
            await serveManifest(http, session, manifestRef);
            return;
          case "insert":
            await serveInsert(session, manifestRef, message.ids);
            return;
          case "brand-templates-request":
          case "brand-templates-refresh":
            await serveBrandTemplates(http, session, brandTemplatesRef);
            return;
          case "switch-brand-template":
            serveSwitchBrandTemplate(session, brandTemplatesRef, message.id);
            return;
          case "presentation-templates-ready":
          case "presentation-templates-refresh":
            await servePresentationManifest(http, session, presentationTemplatesRef);
            return;
          case "presentation-templates-insert":
            await servePresentationInsert(session, presentationTemplatesRef, message.ids);
            return;
          case "close":
            session.close();
        }
      })();
      // serveManifest/serveInsert don't throw, but an unexpected rejection
      // must not become an unhandled promise — the window would just sit on
      // its spinner, so trace it.
      served.catch((error: unknown) =>
        logSlideTemplatesError(
          "slide-templates-dialog-serve-unhandled",
          getErrorMessage(error, `Serving "${message.type}" failed`),
        ),
      );
    },
    [http, onNotice],
  );

  const open = useCallback(() => {
    if (sessionRef.current || isOpeningRef.current) return;

    if (!canUseDialog()) {
      // No dialog window to put the Asset Library in (browser dev, tests).
      logEvent("connector.action.invoke", "info", {
        connectorSystemName: DECKORATE_CONNECTOR,
        reason: "asset-library-open-inline",
      });
      setIsInlineOpen(true);
      return;
    }

    isOpeningRef.current = true;
    openAssetLibraryDialog({
      onMessage: handleMessage,
      onClosed: () => {
        sessionRef.current = null;
        manifestRef.current = [];
        brandTemplatesRef.current = [];
        presentationTemplatesRef.current = [];
        // A staged Brand Templates manifest outliving its window would be
        // shown by a later, unrelated open — Slide/Presentation Templates
        // no longer stage anything (they fetch directly), nothing to clear.
        clearBrandTemplatesHandoff();
        clearPendingBrandTemplatePreview();
        logEvent("connector.action.invoke", "info", {
          connectorSystemName: DECKORATE_CONNECTOR,
          reason: "asset-library-dialog-closed",
        });
      },
    })
      .then((session) => {
        // The pane unmounted while the window was opening — don't leave it up.
        if (!isMountedRef.current) {
          session.close();
          return;
        }
        sessionRef.current = session;
      })
      .catch((error: unknown) => {
        // The window itself couldn't open. There's no dialog to show this in,
        // so the pane's toast is the only place left.
        const message = getErrorMessage(error, "Couldn't open the Asset Library window. Please try again.");
        logSlideTemplatesError("slide-templates-dialog-open", message);
        onNotice(message);
      })
      .finally(() => {
        isOpeningRef.current = false;
      });
  }, [handleMessage, onNotice]);

  const closeInline = useCallback(() => {
    logEvent("connector.action.invoke", "info", {
      connectorSystemName: DECKORATE_CONNECTOR,
      reason: "asset-library-inline-closed",
    });
    setIsInlineOpen(false);
  }, []);

  return { open, isInlineOpen, closeInline };
}
